import { DatePipe } from "@angular/common";
import {
    computed,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    effect,
    inject,
    signal,
    viewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
    CellClickEvent,
    ColumnComponent,
    ColumnReorderEvent,
    ContentScrollEvent,
    GridComponent,
    GridDataResult,
    GridItem,
    PageChangeEvent,
    SelectAllCheckboxState,
} from "@progress/kendo-angular-grid";
import { PopoverContainerDirective, PopoverShownEvent, TooltipDirective } from "@progress/kendo-angular-tooltip";
import { SortDescriptor } from "@progress/kendo-data-query";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { Subscription, animationFrames, asyncScheduler, firstValueFrom, fromEvent, map, observeOn, switchMap, take, tap } from "rxjs";
import {
    AnalyticsService,
    AppSettingsService,
    CardService,
    CategoriesService,
    DashboardsService,
    DeliveryMethodsService,
    FilterSortModalHandlerService,
    FiltersService,
    IFirmDeliveryMethodsService,
    IFirmGroupsService,
    IFirmRolesService,
    IfirmTaxCardsService,
    isUnassigned,
    LocalStorageService,
    MANAGER_ROLE_ID,
    MappingOfStatusesService,
    PARTNER_ROLE_ID,
    ResponseErrorHandlerService,
    TaxReturnStatusesService,
    UnassignedUserId,
    UsersService,
    WkTranslatePipe,
} from "@shared/index";
import { PendingChangesPopupComponent } from "src/app/shared/pending-changes-popup/pending-changes-popup.component";
import { applicationStringFromApplicationType, isJobsBilling } from "src/app/views-interfaces/ApplicationType.enum";
import { ICategories } from "src/app/views-interfaces/categories.interface";
import { IDashboardModel, isiFirmTaxDashboard } from "src/app/views-interfaces/dashboard-model.interface";
import { TranslateObject } from "src/app/views-interfaces/translate-object.interface";
import { AuthService } from "../../shared/auth/auth.service";
import { CardDisplayService } from "../../shared/card-display/card-display.service";
import { CardViewService } from "../../shared/card-view-service/card-view.service";
import { ColumnDataLayoutService } from "../../shared/column-data-layout-services/column-data-layout.service";
import { CountersService } from "../../shared/counters-service/counters.service";
import { HelpersService } from "../../shared/helpers-service/helpers.service";
import { CardEventType } from "../../shared/interfaces/card-event-type.enum";
import { IStatusObjectModel } from "../../views-interfaces/status-object-model.interface";
import { IStepsModel } from "../../views-interfaces/steps-model.interface";
import { ITaxReturnStatus } from "../../views-interfaces/tax-return-status-model.interface";
import { AllView, CompareTimestampOptions } from "./all-view";
import { JobsAndBillingJobTypesService } from "@shared/jobs-and-billing-job-types-service";
import { JobsAndBillingPriorityService } from "@shared/jobs-and-billing-priority";
import { JobsAndBillingCommonService } from "@app/shared/jobs-and-billing-common-service/jobs-and-billing-common.service";
import { isTaxprepT1Card } from "@app/views-interfaces";
import { compareString } from "@shared/utils";

export interface RoleMappingColumns {
    RoleId: string;
    RoleName: string;
}

interface SortField {
    field: string;
    type: string;
    removeTime: boolean;
    clientNameComparison: boolean;
    timeStampComparison: boolean;
    resource: string | undefined;
    label?: string;
    orderField: string;
    orderFunc?: (values: any) => any;
    valueField: string;
    valueFunc?: (values: any) => any;
    isActive: boolean | ((values: any) => boolean);
    export: boolean;
    isVisible: boolean;
    width: number;
    locked: boolean;
    reorderable: boolean;
}

interface SettingItem {
    field: string;
    label: string;
    selected: boolean;
    order: number;
}

interface ViewportAnchor {
    index: number;
    offset: number;
}

@Component({
    selector: "wk-all-view",
    templateUrl: "./all-view.component.html",
    styleUrl: "./all-view.component.scss",
    standalone: false,
    host: { class: "wk-av" },
    providers: [WkTranslatePipe],
})
export class AllViewComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly ngZone = inject(NgZone);
    private readonly authService = inject(AuthService);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly cardViewService = inject(CardViewService);
    private readonly columnDataLayoutService = inject(ColumnDataLayoutService);
    protected readonly dashboardsService = inject(DashboardsService);
    private readonly deliveryMethodsService = inject(DeliveryMethodsService);
    private readonly iFirmDeliveryMethodsService = inject(IFirmDeliveryMethodsService);
    private readonly ifirmTaxCardsService = inject(IfirmTaxCardsService);
    private readonly modalService = inject(BsModalService);
    private readonly modalCoordinationService = inject(FilterSortModalHandlerService);
    private readonly router = inject(Router);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);
    private readonly jobsAndBillingPriorityService = inject(JobsAndBillingPriorityService);
    private readonly wkTranslatePipe = inject(WkTranslatePipe);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);

    analyticsService = inject(AnalyticsService);
    cardService = inject(CardService);
    categoriesService = inject(CategoriesService);
    countersService = inject(CountersService);
    datePipe = inject(DatePipe);
    filtersService = inject(FiltersService);
    helpersService = inject(HelpersService);
    iFirmGroupsService = inject(IFirmGroupsService);
    mappingOfStatusesService = inject(MappingOfStatusesService);
    responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    taxReturnStatusesService = inject(TaxReturnStatusesService);
    private readonly translateService = inject(TranslateService);
    private readonly usersService = inject(UsersService);

    cardData: Map<string, any> = new Map<string, any>();
    selectedStep = null;

    public tooltipDir = viewChild<TooltipDirective>(TooltipDirective);
    public readonly viewAllGrid = viewChild<GridComponent>(GridComponent);
    public readonly container = viewChild<PopoverContainerDirective>("popoverContainer");
    public readonly popupAnchor = viewChild<ElementRef>("popupAnchor");

    cardChangedSubscription: Subscription;
    filtersSubscription: Subscription;
    loadCardsFromCacheSubscription: Subscription;
    private longOperationSubscription: Subscription;
    private langChangeSubscription: Subscription;
    public readonly AllCardView = signal(new Map<string, AllView>());
    public readonly sort = signal<SortDescriptor[]>([
        {
            field: "TimeStamp",
            dir: "desc",
        },
    ]);
    // Keep virtual scrolling on the published snapshot while card updates accumulate.
    public readonly data = signal<AllView[]>([]);
    public readonly currentCounter = computed(() => this.data().length);
    public allCardViewLoaded = false;
    public columns: RoleMappingColumns[] = [];
    public IsLoaded = false;
    public Group1: string;
    public Group2: string;
    public isGroup1Enabled = false;
    public isGroup2Enabled = false;
    public isJobsGroup1Enabled = false;
    public isJobsGroup2Enabled = false;
    public deliveryMethods;
    public settingPageNumber = 1;
    public maxSettingPageSize = 1;
    public settingPagedItems: any[];
    public settingItems: SettingItem[] = [];
    public initialItemsOrder: SettingItem[] = [];
    public searchText = "";
    public totalItemsCount = 0;

    public clientCodeToUse = "";
    public CurrentDeletionCount: string[];
    private taxReturnStatusId: Map<string, ITaxReturnStatus> = new Map<string, ITaxReturnStatus>();
    private steps: Map<string, IStepsModel> = new Map<string, IStepsModel>();
    private statuses: Map<string, IStatusObjectModel> = new Map<string, IStatusObjectModel>();
    private categories: Map<string, ICategories> = new Map<string, ICategories>();
    private categoriesFromService: ICategories[];

    public readonly loading = signal(false);
    public readonly skip = signal(0);
    public readonly pageSize = signal(60);
    public readonly rowHeight = 36;
    public settingPageSize = 10;
    public readonly gridData = computed<GridDataResult>(() => {
        const data = this.data();
        const skip = this.skip();

        return {
            data: data.slice(skip, skip + this.pageSize()),
            total: data.length,
        };
    });
    public mySelection = [];
    public selectAllState: SelectAllCheckboxState = "unchecked";
    public opened = false;
    public isExportPopupOpened = false;
    public longOperationInProgress = false;
    public delayedCardRefreshTimer;
    private delayedCardRefreshMaxTimer;
    private delayedGridPublicationTimer: ReturnType<typeof setTimeout> | undefined;
    private modelRevision = 0;
    private publishedRevision = 0;
    private viewGeneration = 0;
    private refreshGeneration = 0;
    private refreshIntent: "background" | "explicit" | "rebuild" = "background";
    private readonly cardRevisions = new Map<string, number>();
    private lastScrollTop = 0;
    private pendingViewportRead = false;
    private pendingPosition: ViewportAnchor | undefined;
    private scrollEndSubscription: Subscription | undefined;
    private positionSubscription: Subscription | undefined;
    private destroyed = false;
    private dateFormat = "yyyy-MM-dd";
    private dateTimeFormat = "yyyy-MM-dd hh:mm a";
    public ShowLastYearReceivedDate: boolean;
    public isiFirmDashboard: boolean;
    private currentDashboard: IDashboardModel;
    private returnStatuses = [];
    public userCanDeleteCard = false;
    public userCanPrintExport = false;
    public userCanBulkChange = false;
    public isActionsMenuOpen = signal<boolean>(false);
    public readonly isGridDataAvailable = signal(false);
    currentLanguage = "en-CA";
    private hasPendingFilterChanges = false;
    private favouriteFiltersPendingChangesEvent: Subscription;
    private bsModalRef?: BsModalRef;
    public isBulkChangeOpen = signal<boolean>(false);
    protected isJobsBillingDashboard = false;

    constructor() {
        this.cardChangedSubscription = this.cardDisplayService.cardChangedEvent$.subscribe((event) => {
            if (!this.longOperationInProgress) {
                switch (event.eventType) {
                    case CardEventType.cardUpdated:
                        this.handleCardUpdated(event.newCard);
                        break;
                    case CardEventType.cardAdded:
                        this.handleCardAdded(event.newCard);
                        break;
                    case CardEventType.cardDeleted:
                        this.handleCardDeleted(event.oldCard);
                        break;
                }
            }
        });
        this.longOperationSubscription = this.authService.longOperationEvent$.subscribe((e) => {
            if (e) {
                this.longOperationInProgress = true;
            } else if (this.longOperationInProgress) {
                this.longOperationInProgress = false;
                this.delayedCardRefresh();
            }
        });
        this.filtersSubscription = this.filtersService.filtersChangedEvent$.subscribe(() => {
            if (this.allCardViewLoaded) {
                this.loadCardData();
            }
        });
        this.loadCardsFromCacheSubscription = this.columnDataLayoutService.loadCardsFromCacheEvent$.subscribe((event) => {
            // SignalR card events are applied individually above; reloading the full cache reorders the virtual grid.
            if (!this.longOperationInProgress && !Array.isArray(event)) {
                this.delayedCardRefresh();
            }
        });

        effect(() => {
            if (this.AllCardView().size > 0 && !this.allCardViewLoaded) {
                this.allCardViewLoaded = true;
            }
        });
    }

    delayedCardRefresh() {
        if (!this.IsLoaded) return;
        if (this.delayedCardRefreshTimer) {
            clearTimeout(this.delayedCardRefreshTimer);
        }
        // To avoid multiple loadCardData
        this.delayedCardRefreshTimer = setTimeout(() => {
            this.executeDelayedCardRefresh();
        }, 1000);

        // Guarantee a refresh at least every 5s even if updates keep resetting the debounce timer
        if (!this.delayedCardRefreshMaxTimer) {
            this.delayedCardRefreshMaxTimer = setTimeout(() => {
                this.executeDelayedCardRefresh();
            }, 5000);
        }
    }

    private executeDelayedCardRefresh() {
        this.cancelDelayedCardRefresh();
        this.loadCardData(null, false, "background");
    }

    private cancelDelayedCardRefresh(): void {
        if (this.delayedCardRefreshTimer) {
            clearTimeout(this.delayedCardRefreshTimer);
            this.delayedCardRefreshTimer = undefined;
        }
        if (this.delayedCardRefreshMaxTimer) {
            clearTimeout(this.delayedCardRefreshMaxTimer);
            this.delayedCardRefreshMaxTimer = undefined;
        }
    }

    async ngAfterViewInit() {
        if (this.authService.loggedIn()) {
            await this.init();
        }
        this.loadColumnName();
        this.loadItemsFromLocalStorage();
        this.loadInitialItemsOrder();
        this.setColumnOrder(this.settingItems);
        this.adjustLastColumn();
        this.loadSortSelectionFromLocalStorage();
    }

    async init() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isJobsBillingDashboard = isJobsBilling(this.currentDashboard.ApplicationType);
        if (this.isJobsBillingDashboard) {
            await this.jobsAndBillingJobTypesService.getJobTypes();
            await this.jobsAndBillingJobTypesService.getJobRoleStatusAssociation();
        }
        this.taxReturnStatusesService.refresh();
        await this.iFirmGroupsService.getiFirmGroups();
        await this.iFirmRolesService.getiFirmRoles();
        await this.dashboardsService.getCurrentDashboard();
        this.isiFirmDashboard = isiFirmTaxDashboard(this.currentDashboard);
        this.ShowLastYearReceivedDate = !this.isiFirmDashboard || this.currentDashboard.TaxYear >= 2025;
        this.clientCodeToUse = this.currentDashboard.ClientCode;
        this.analyticsService.track("Viewed all returns");
        if (this.isiFirmDashboard && this.currentDashboard.TaxYear >= 2025) {
            this.deliveryMethods = (await this.iFirmDeliveryMethodsService.getiFirmDeliveryMethods()).reduce((deliveryMethods, method) => {
                deliveryMethods[method.Id] = method as TranslateObject;
                return deliveryMethods;
            }, {});
        } else {
            this.deliveryMethods = this.deliveryMethodsService.getDeliveryMethodsList(this.currentDashboard);
        }
        this.categoriesFromService = await this.categoriesService.getCategoriesList();
        this.categoriesFromService.forEach((categ) => {
            this.categories[categ.Id] = categ;
        });
        if (!this.helpersService.categoriesInitialized) {
            await this.helpersService.initializeCategories();
        }
        (await this.taxReturnStatusesService.getTaxReturnStatusesList()).forEach((taxReturnStatus) => {
            if (!taxReturnStatus || !taxReturnStatus.Id) return;
            this.taxReturnStatusId.set(taxReturnStatus.Id, taxReturnStatus);
            this.steps.set(taxReturnStatus.Id, this.mappingOfStatusesService.getStepFromStatusId(taxReturnStatus.Id));
            this.statuses.set(taxReturnStatus.Id, this.mappingOfStatusesService.getStatusFromStatusId(taxReturnStatus.Id));
        });
        if (!this.helpersService.stepsMerged) {
            await this.helpersService.mergeSteps();
        }
        this.mappingOfStatusesService.getAllReturnStatuses().forEach((status) => {
            if (status.TaxReturnStatus) {
                this.returnStatuses.push(status.TaxReturnStatus.Id);
            }
        });
        this.setGroups();
        this.setRoles();
        this.IsLoaded = true;
        await this.loadCardData();
    }

    ngOnInit() {
        this.cardViewService.setCurrentViewType("all");
        this.isGridDataAvailable.set(false);
        this.currentLanguage = this.helpersService.getLang();
        this.dateFormat = this.currentLanguage === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy";
        this.dateTimeFormat = this.currentLanguage === "en-CA" ? "MMMM d, yyyy hh:mm a" : "d MMMM yyyy HH:mm";
        this.analyticsService.track("Viewed All View");
        this.langChangeSubscription = this.translateService.onLangChange.subscribe(async () => {
            this.currentLanguage = this.helpersService.getLang();
            this.dateFormat = this.currentLanguage === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy";
            this.dateTimeFormat = this.currentLanguage === "en-CA" ? "MMMM d, yyyy hh:mm a" : "d MMMM yyyy HH:mm";
            await this.refreshAllViewModel();
            this.loadColumnName();
        });
        this.userCanDeleteCard = this.authService.currentUser.can_delete_cards === "True";
        this.userCanPrintExport = this.authService.currentUser.can_print_export === "True";
        this.userCanBulkChange =
            this.authService.currentUser.can_bulk_change_cards === "True" && this.appSettingsService.appSettings.Enable_Bulk_Change === "true";

        this.favouriteFiltersPendingChangesEvent = this.helpersService.favouriteFiltersPendingChangesEvent$.subscribe((hasPendingChanges) => {
            this.hasPendingFilterChanges = hasPendingChanges;
        });
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.viewGeneration++;
        this.resetViewportTracking();
        this.cancelDelayedCardRefresh();
        this.cancelDelayedGridPublication();
        this.filtersSubscription.unsubscribe();
        this.cardChangedSubscription.unsubscribe();
        this.loadCardsFromCacheSubscription.unsubscribe();
        this.favouriteFiltersPendingChangesEvent?.unsubscribe();
        this.longOperationSubscription?.unsubscribe();
        this.langChangeSubscription?.unsubscribe();
    }

    @HostListener("window:resize")
    onResize() {
        this.adjustLastColumn(true);
    }

    // Handle signalR for cards
    handleCardAdded(data) {
        this.loadCardData(data);
    }

    handleCardUpdated(data) {
        this.loadCardData(data);
    }

    handleCardDeleted(data) {
        if (this.cardData.has(data.cardId)) {
            if (this.mySelection.includes(data.cardId)) {
                this.mySelection.splice(this.mySelection.indexOf(data.cardId), 1);
                this.onSelectedKeysChange();
            }
            this.cardDisplayService.removeCardById(data.cardId);
        }
        this.loadCardData(data, true);
    }

    setSettingPagedItems() {
        const startIndex = (this.settingPageNumber - 1) * this.settingPageSize;
        this.settingPagedItems = this.settingItems.filter((item) => item.label.toLowerCase().includes(this.searchText.toLowerCase()));
        this.totalItemsCount = this.settingPagedItems.length;
        this.maxSettingPageSize = Math.ceil(this.settingPagedItems.length / this.settingPageSize);
        this.settingPagedItems = this.settingPagedItems.slice(startIndex, this.settingPageNumber * this.settingPageSize);
    }

    onResetColumnHandler() {
        this.analyticsService.track("Reset columns All View");
        this.setColumnOrder(this.initialItemsOrder);
        this.loadColumnName();
        setTimeout(() => {
            this.setItemstoLocalStorage();
        });
        this.adjustLastColumn();
    }

    loadInitialItemsOrder() {
        let counter = 0;
        setTimeout(() => {
            this.viewAllGrid()?.columnList?.forEach((column) => {
                if (!column.locked && !column.sticky) {
                    this.initialItemsOrder.push({
                        field: (column as ColumnComponent).field,
                        label: column.title,
                        selected: !column.hidden,
                        order: counter,
                    });
                }
                counter++;
            });
        });
    }

    setColumnOrder(items: any) {
        if (items.length > 0) {
            let maxOrder = Math.max(
                ...items.map(function (o) {
                    return o.order;
                }),
            );
            this.viewAllGrid()?.columnList?.forEach((column) => {
                if (!column.locked && !column.sticky) {
                    const currentcolumn = items.find((role) => role.field === (column as ColumnComponent).field);
                    if (currentcolumn) {
                        if (maxOrder !== 0 && currentcolumn.order === 0) {
                            column.orderIndex = ++maxOrder;
                        } else {
                            column.orderIndex = currentcolumn.order;
                        }
                        column.hidden = !currentcolumn.selected;
                    }
                } else if (column.locked) {
                    column.orderIndex = 0;
                } else if (column.sticky) {
                    column.orderIndex = 300;
                }
            });
        }
    }

    prepareTagModel(T1Tags): any {
        return T1Tags.map((tag) => {
            return {
                Id: tag.Id,
                Name: {
                    "en-CA": tag.CaptionEnglish,
                    "fr-CA": tag.CaptionFrench,
                },
                Description: {
                    "en-CA": tag.DescriptionEnglish,
                    "fr-CA": tag.DescriptionFrench,
                },
                BackgroundColor: tag.BackgrounColor,
                ForegroundColor: tag.ForegroundColor,
                TaxpayerCell: tag.TaxpayerCell,
                TaxpayerPrevYearCell: tag.TaxpayerPrevYearCell,
                YearToCheck: tag.YearToCheck,
            };
        });
    }

    pageChange(e: PageChangeEvent): void {
        if (this.destroyed) return;
        this.skip.set(Math.min(e.skip, Math.max(0, this.data().length - this.pageSize())));
        if (this.pendingViewportRead || this.pendingPosition) {
            this.ngZone.run(() =>
                this.afterGridLayout(() => {
                    if (this.pendingViewportRead) {
                        this.publishReachedViewport();
                    } else {
                        this.restoreGridPosition();
                    }
                }),
            );
        }
    }

    public onContentScroll(event: ContentScrollEvent): void {
        const previousTop = this.lastScrollTop;
        this.lastScrollTop = event.scrollTop;
        if (this.destroyed || event.scrollTop === previousTop) {
            return;
        }

        this.cancelViewportPosition();
        if (this.modelRevision === this.publishedRevision || !this.isGridDataAvailable()) return;
        const content = this.getGridContent();
        if (!content) {
            console.warn("Unable to preserve All View position: grid content is unavailable.");
            return;
        }

        // Finish native motion and leave the current paint before Kendo requests a differently positioned page.
        this.scrollEndSubscription = fromEvent(content, "scrollend")
            .pipe(
                take(1),
                // Some browsers dispatch the last scroll event after scrollend at this same position.
                tap(() => (this.lastScrollTop = content.scrollTop)),
                switchMap(() => animationFrames().pipe(take(1))),
                observeOn(asyncScheduler),
            )
            .subscribe(() => {
                this.scrollEndSubscription = undefined;
                const anchor = this.getViewportAnchor(content);
                if (anchor) this.publishAnchoredData(anchor);
                else {
                    this.pendingViewportRead = true;
                    this.ngZone.run(() => this.afterGridLayout(() => this.publishReachedViewport()));
                }
            });
    }

    private publishReachedViewport(): void {
        if (!this.pendingViewportRead) return;
        const content = this.getGridContent();
        const anchor = content && this.getViewportAnchor(content);
        this.pendingViewportRead = false;
        if (anchor) this.publishAnchoredData(anchor);
        else console.warn("Unable to locate the All View viewport after virtual page rendering.");
    }

    private publishAnchoredData(anchor: ViewportAnchor): void {
        const generation = this.viewGeneration;
        const snapshot = this.data();
        const data = this.sortCards();
        const indexes = new Map(data.map((card, index) => [card.CardId, index]));
        let index = indexes.get(snapshot[anchor.index]?.CardId);
        for (let next = anchor.index + 1; index === undefined && next < snapshot.length; next++) {
            index = indexes.get(snapshot[next].CardId);
        }
        for (let previous = anchor.index - 1; index === undefined && previous >= 0; previous--) {
            index = indexes.get(snapshot[previous].CardId);
        }

        const offset = index === undefined ? 0 : anchor.offset;
        const row = index ?? 0;
        this.ngZone.run(() => {
            // Kendo owns the buffered skip and virtual table offset; let its scroll request move the page.
            this.publishGridData(data);
            if (this.destroyed || generation !== this.viewGeneration) return;
            this.pendingPosition = { index: row, offset };
            this.afterGridLayout(() => this.restoreGridPosition());
        });
    }

    private afterGridLayout(callback: () => void): void {
        this.positionSubscription?.unsubscribe();
        const generation = this.viewGeneration;
        this.changeDetector.detectChanges();
        if (this.destroyed || generation !== this.viewGeneration) return;
        // Kendo measures and synchronizes locked rows on zone stability, after Angular has rendered the new data.
        this.positionSubscription = this.ngZone.onStable.pipe(take(1)).subscribe(() => {
            this.positionSubscription = undefined;
            if (!this.destroyed && generation === this.viewGeneration) callback();
        });
    }

    private restoreGridPosition(): void {
        const position = this.pendingPosition;
        const grid = this.viewAllGrid();
        const content = this.getGridContent();
        if (!position) return;
        if (!grid || !content) {
            this.cancelViewportPosition();
            console.warn("Unable to restore All View position: grid content is unavailable.");
            return;
        }

        if (this.data().length > 0) {
            grid.scrollTo({ row: position.index });
            const element = content.querySelector<HTMLTableRowElement>(`tr[data-kendo-grid-item-index="${position.index}"]`);
            if (element) {
                const distance = element.getBoundingClientRect().top - content.getBoundingClientRect().top - content.clientTop;
                content.scrollTop = Math.max(
                    0,
                    Math.min(content.scrollTop + distance + position.offset, content.scrollHeight - content.clientHeight),
                );
                this.pendingPosition = undefined;
            }
        } else {
            content.scrollTop = 0;
            this.pendingPosition = undefined;
        }
        if (!this.pendingPosition) {
            const wrapper: HTMLElement = grid.wrapper.nativeElement;
            const locked = wrapper.querySelector<HTMLElement>(".k-grid-content-locked");
            if (locked) locked.scrollTop = content.scrollTop;
        }
        this.lastScrollTop = content.scrollTop;
    }

    private getGridContent(): HTMLElement | undefined {
        const wrapper: HTMLElement | undefined = this.viewAllGrid()?.wrapper.nativeElement;
        return wrapper?.querySelector<HTMLElement>(".k-grid-content") ?? undefined;
    }

    private getViewportAnchor(content: HTMLElement): ViewportAnchor | undefined {
        const top = content.getBoundingClientRect().top + content.clientTop;
        for (const row of content.querySelectorAll<HTMLTableRowElement>("tr[data-kendo-grid-item-index]")) {
            const bounds = row.getBoundingClientRect();
            if (bounds.top <= top && bounds.bottom > top) {
                return { index: Number(row.getAttribute("data-kendo-grid-item-index")), offset: top - bounds.top };
            }
        }

        return undefined;
    }

    private resetViewportTracking(): void {
        this.cancelViewportPosition();
        this.lastScrollTop = this.getGridContent()?.scrollTop ?? 0;
    }

    private cancelViewportPosition(): void {
        this.scrollEndSubscription?.unsubscribe();
        this.scrollEndSubscription = undefined;
        this.positionSubscription?.unsubscribe();
        this.positionSubscription = undefined;
        this.pendingViewportRead = false;
        this.pendingPosition = undefined;
    }

    private saveSortToLocalStorage() {
        const storageKey = this.getLocalStorageKey();
        LocalStorageService.setItem(storageKey + "AllViewSort", JSON.stringify(this.sort()));
    }

    private loadSortSelectionFromLocalStorage() {
        const storageKey = this.getLocalStorageKey();
        const sortValue = localStorage.getItem(storageKey + "AllViewSort");
        if (sortValue) {
            this.sort.set(JSON.parse(sortValue));
            this.publishGridData();
        }
    }

    protected getValueForSort(values: AllView, field: string): any {
        let value: any;
        const fields = field.split(".");
        fields.forEach((f) => {
            value = value ? value[f] : (values as any)[f];
        });
        return value;
    }

    private _fields = computed<Map<string, SortField>>(() => {
        const isJobsBilling = this.dashboardsService.isJobsBilling();
        const map = new Map(
            [
                {
                    field: "ClientCode",
                    valueField: `ClientCode.${this.clientCodeToUse}`,
                    type: "number",
                    clientNameComparison: false,
                    locked: true,
                    reorderable: false,
                },
                {
                    field: "ClientFileCount",
                    valueField: "CalcFields.clientFileCount",
                    type: "number",
                    label: '<i class="fa fa-user wk-taxpayer-count wk-av__columns--i" aria-hidden="true"></i>',
                    export: false,
                    isVisible: !isJobsBilling,
                    width: 50,
                    locked: true,
                    reorderable: false,
                },
                {
                    field: "JobID",
                    type: "number",
                    isVisible: isJobsBilling,
                    width: 80,
                    locked: true,
                    reorderable: false,
                },
                {
                    field: "ClientName",
                    type: "string",
                    clientNameComparison: false,
                    width: 200,
                    locked: true,
                    reorderable: false,
                },
                {
                    field: "ReturnType",
                    valueFunc: (values: any) => this.translateService.instant("Board." + values.return.type),
                    orderField: "return.type",
                    type: "string",
                    resource: "FamilyMember",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "JobType",
                    type: "string",
                    isVisible: isJobsBilling,
                    width: 150,
                },
                {
                    field: "ReturnStatus",
                    valueFunc: (values: any) => this.wkTranslatePipe.transform(values.ReturnStatus),
                    orderField: "ReturnStatusOrder",
                    type: "number",
                    resource: !isJobsBilling ? "ReturnStatus" : "JobStatus",
                    width: 150,
                },
                {
                    field: "Step",
                    valueFunc: (values: any) => this.wkTranslatePipe.transform(values.Step),
                    orderField: "StepOrder",
                    type: "number",
                },
                {
                    field: "DaysSinceInStep",
                    valueFunc: (values: any) => values.CalcFields?.daysSinceInStep + this.translateService.instant("CardModal.Days"),
                    orderField: "CalcFields.daysSinceInStep",
                    type: "number",
                    resource: "DaysInStep",
                },
                {
                    field: "VIP",
                    type: "boolean",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "Priority",
                    orderField: "PriorityRaw",
                    type: "string",
                    isVisible: isJobsBilling,
                },
                {
                    field: "ContactPartner",
                    type: "string",
                    isVisible: isJobsBilling,
                    isActive: (values: any) => (values.ContactPartner ? this.getUserActiveByRole(values.ContactPartnerId, PARTNER_ROLE_ID) : true),
                },
                {
                    field: "ContactManager",
                    type: "string",
                    isVisible: isJobsBilling,
                    isActive: (values: any) => (values.ContactManager ? this.getUserActiveByRole(values.ContactManagerId, MANAGER_ROLE_ID) : true),
                },
                {
                    field: "CurrentlyResponsible",
                    type: "string",
                    isVisible: isJobsBilling,
                    isActive: (values: any) => (values.CurrentlyResponsible ? this.getUserActiveById(values.CurrentlyResponsibleId) : true),
                },
                {
                    field: "JobPartner",
                    type: "string",
                    isVisible: isJobsBilling,
                    isActive: (values: any) => (values.JobPartner ? this.getUserActiveByRole(values.JobPartnerId, PARTNER_ROLE_ID) : true),
                },
                {
                    field: "JobManager",
                    type: "string",
                    isVisible: isJobsBilling,
                    isActive: (values: any) => (values.JobManager ? this.getUserActiveByRole(values.JobManagerId, MANAGER_ROLE_ID) : true),
                },
                {
                    field: "JobOwner",
                    type: "string",
                    isVisible: isJobsBilling,
                    isActive: (values: any) => (values.JobOwner ? this.getUserActiveById(values.JobOwnerId) : true),
                },
                {
                    field: "Category",
                    valueFunc: (values: any) =>
                        values.Category === "Unassigned"
                            ? this.translateService.instant("Unassigned")
                            : (this.wkTranslatePipe.transform(values.Category) ?? ""),
                    orderField: "CategoryOrder",
                    type: "number",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "ContactGroup1Option",
                    orderFunc: (values: any) =>
                        values.ContactGroup1Id && values.ContactGroup1Id !== "-1" && values.ContactGroup1Id !== "0"
                            ? values.ContactGroup1Option.toLowerCase()
                            : "",
                    type: "string",
                    label: isJobsBilling ? this.translateService.instant("JobsAndBilling.Contact.Title") + " " + this.Group1 : this.Group1,
                    isActive: (values: any) => (values.ContactGroup1Option ? this.getGroup1ActiveById(values.ContactGroup1Id) : true),
                },
                {
                    field: "ContactGroup2Option",
                    orderFunc: (values: any) =>
                        values.ContactGroup2Id && values.ContactGroup2Id !== "-1" && values.ContactGroup2Id !== "0"
                            ? values.ContactGroup2Option.toLowerCase()
                            : "",
                    type: "string",
                    label: isJobsBilling ? this.translateService.instant("JobsAndBilling.Contact.Title") + " " + this.Group2 : this.Group2,
                    isActive: (values: any) => (values.ContactGroup2Option ? this.getGroup2ActiveById(values.ContactGroup2Id) : true),
                },
                {
                    field: "JobGroup1Option",
                    orderFunc: (values: any) => (values.JobGroup1Id && values.JobGroup1Id !== "-1" ? values.JobGroup1Option.toLowerCase() : ""),
                    type: "string",
                    label: this.translateService.instant("JobsAndBilling.Job") + " " + this.Group1,
                    isVisible: isJobsBilling && this.isJobsGroup1Enabled,
                    isActive: (values: any) => (values.JobGroup1Option ? this.getGroup1ActiveById(values.JobGroup1Id) : true),
                },
                {
                    field: "JobGroup2Option",
                    orderFunc: (values: any) => (values.JobGroup2Id && values.JobGroup2Id !== "-1" ? values.JobGroup2Option.toLowerCase() : ""),
                    type: "string",
                    label: this.translateService.instant("JobsAndBilling.Job") + " " + this.Group2,
                    isVisible: isJobsBilling && this.isJobsGroup2Enabled,
                    isActive: (values: any) => (values.JobGroup2Option ? this.getGroup2ActiveById(values.JobGroup2Id) : true),
                },
                {
                    field: "T1",
                    valueFunc: (values: any) => (values.t1 ? (this.wkTranslatePipe.transform(values.t1) ?? "") : ""),
                    type: "string",
                    resource: "T1EFILE",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "TP1",
                    valueFunc: (values: any) => (values.tp1 ? (this.wkTranslatePipe.transform(values.tp1) ?? "") : ""),
                    type: "string",
                    resource: "TP1EFILE",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "TimeStamp",
                    orderField: "TimeStampRaw",
                    type: "timestamp",
                    timeStampComparison: false,
                    resource: "DateModified",
                },
                {
                    field: "LastYearDocumentsReceivedDate",
                    valueFunc: (values: any) =>
                        values.LastYearDocumentsReceivedDate
                            ? this.datePipe.transform(
                                  values.LastYearDocumentsReceivedDate * 1000,
                                  this.currentLanguage === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
                                  "",
                                  this.currentLanguage,
                              )
                            : "",
                    orderField: "LastYearDocumentsReceivedDate",
                    type: "timestamp",
                    resource: "LastYearReceivedDate",
                    isVisible: !isJobsBilling && this.ShowLastYearReceivedDate,
                },
                {
                    field: "TargetStartDate",
                    orderField: "TargetStartDateRaw",
                    type: "timestamp",
                    removeTime: true,
                    isVisible: isJobsBilling,
                },
                {
                    field: "TargetEndDate",
                    orderField: "TargetEndDateRaw",
                    type: "timestamp",
                    removeTime: true,
                    isVisible: isJobsBilling,
                },
                {
                    field: "PeriodEndDate",
                    orderField: "PeriodEndDateRaw",
                    type: "timestamp",
                    removeTime: true,
                    isVisible: isJobsBilling,
                },
                {
                    field: "DateReceived",
                    orderField: "DateReceivedRaw",
                    type: "timestamp",
                    removeTime: true,
                },
                {
                    field: "DateNeeded",
                    valueFunc: (values: any) =>
                        values.DateNeeded
                            ? this.datePipe.transform(
                                  values.DateNeeded * 1000,
                                  this.currentLanguage === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
                                  "",
                                  this.currentLanguage,
                              )
                            : "",
                    type: "timestamp",
                    removeTime: true,
                    isVisible: !isJobsBilling,
                },
                {
                    field: "Deadline",
                    valueFunc: (values: any) =>
                        values.Deadline
                            ? this.datePipe.transform(
                                  values.Deadline * 1000,
                                  this.currentLanguage === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
                                  "",
                                  this.currentLanguage,
                              )
                            : "",
                    type: "timestamp",
                    removeTime: true,
                    isVisible: !isJobsBilling,
                },
                {
                    field: "DeliveryMethod",
                    type: "string",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "ThisYearCompletionDateTimestamp",
                    valueFunc: (values: any) =>
                        values.ThisYearCompletionDateTimestamp
                            ? this.datePipe.transform(
                                  values.ThisYearCompletionDateTimestamp * 1000,
                                  this.currentLanguage === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
                                  "",
                                  this.currentLanguage,
                              )
                            : "",
                    type: "timestamp",
                    removeTime: true,
                    resource: "ThisYearCompletionDate",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "LastYearCompletedDate",
                    valueFunc: (values: any) =>
                        values.LastYearCompletedDate
                            ? this.datePipe.transform(
                                  values.LastYearCompletedDate * 1000,
                                  this.currentLanguage === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
                                  "",
                                  this.currentLanguage,
                              )
                            : "",
                    type: "timestamp",
                    removeTime: true,
                    resource: "LastYearCompletionDate",
                    isVisible: !isJobsBilling,
                },
                {
                    field: "DaysSinceReady",
                    valueFunc: (values: any) =>
                        values.DaysSinceReady ? values.DaysSinceReady + this.translateService.instant("CardModal.Days") : "",
                    orderField: "CalcFields.daysSinceReady",
                    type: "number",
                    resource: "WorkCycleTime",
                    isVisible: !isJobsBilling,
                },
            ].map(
                (item) =>
                    [
                        item.field,
                        {
                            ...item,
                            clientNameComparison: "clientNameComparison" in item ? item.clientNameComparison : true,
                            timeStampComparison: "timeStampComparison" in item ? item.timeStampComparison : true,
                            removeTime: "removeTime" in item ? item.removeTime : false,
                            orderField: "orderField" in item ? item.orderField : "valueField" in item ? item.valueField : item.field,
                            orderFunc: "orderFunc" in item ? item.orderFunc : undefined,
                            export: "export" in item ? item.export : true,
                            resource: !("label" in item) ? ("resource" in item ? item.resource : item.field) : undefined,
                            isVisible: "isVisible" in item ? item.isVisible : true,
                            valueField: "valueField" in item ? item.valueField : item.field,
                            width: "width" in item ? item.width : 170,
                            locked: "locked" in item ? item.locked : false,
                            reorderable: "reorderable" in item ? item.reorderable : true,
                            isActive: "isActive" in item ? item.isActive : true,
                        },
                    ] as [string, SortField],
            ),
        );

        if (!isJobsBilling) {
            this.columns.forEach((column) => {
                const field = `RoleUserMapping_${column.RoleId}`;
                if (!map.has(field)) {
                    map.set(field, {
                        field: field,
                        valueField: field,
                        orderField: field,
                        type: "string",
                        label: column.RoleName,
                        valueFunc: (values: any) => this.getRoleData(values.RoleUserMapping, column.RoleId),
                        isActive: (values: any) => this.getUserData(values.RoleUserMapping, column.RoleId),
                        export: true,
                        isVisible: true,
                        width: 220,
                        locked: false,
                        reorderable: true,
                        clientNameComparison: true,
                        timeStampComparison: true,
                        removeTime: false,
                    } as SortField);
                }
            });
        }

        return map;
    });

    protected fields = computed(() => Array.from(this._fields().values()));

    private sortField(a: AllView, b: AllView, field: string, dir: "asc" | "desc", clientNameComparison = true, timeStampComparison = true): number {
        const fieldInfo = this._fields().get(field);
        if (!fieldInfo) return 0;
        if (field.startsWith("RoleUserMapping_")) {
            const roleId = field.substring(16);

            const aValue = fieldInfo.valueFunc ? fieldInfo.valueFunc(a) : this.getRoleData(a.RoleUserMapping, roleId);
            const bValue = fieldInfo.valueFunc ? fieldInfo.valueFunc(b) : this.getRoleData(b.RoleUserMapping, roleId);
            const aUserActive =
                fieldInfo.isActive && typeof fieldInfo.isActive === "function" ? fieldInfo.isActive(a) : this.getUserData(a.RoleUserMapping, roleId);
            const bUserActive =
                fieldInfo.isActive && typeof fieldInfo.isActive === "function" ? fieldInfo.isActive(b) : this.getUserData(b.RoleUserMapping, roleId);
            if (aValue && bValue && aUserActive !== bUserActive) {
                const res = !aUserActive ? 1 : -1;
                return dir === "desc" ? -res : res;
            } else return this.compareString(aValue, bValue, dir, a, b, fieldInfo.clientNameComparison, fieldInfo.timeStampComparison);
        }

        const aValue = fieldInfo.orderFunc ? fieldInfo.orderFunc(a) : this.getValueForSort(a, fieldInfo.orderField);
        const bValue = fieldInfo.orderFunc ? fieldInfo.orderFunc(b) : this.getValueForSort(b, fieldInfo.orderField);

        const aActive = fieldInfo.isActive ? (typeof fieldInfo.isActive === "function" ? fieldInfo.isActive(a) : fieldInfo.isActive) : true;
        const bActive = fieldInfo.isActive ? (typeof fieldInfo.isActive === "function" ? fieldInfo.isActive(b) : fieldInfo.isActive) : true;
        if (aValue && bValue && aActive !== bActive) {
            const res = !aActive ? 1 : -1;
            return dir === "desc" ? -res : res;
        }

        switch (fieldInfo.type) {
            case "number":
                return this.compareNumber(
                    aValue,
                    bValue,
                    dir,
                    a,
                    b,
                    fieldInfo.clientNameComparison && clientNameComparison,
                    fieldInfo.timeStampComparison && timeStampComparison,
                );
            case "string":
                return this.compareString(
                    aValue,
                    bValue,
                    dir,
                    a,
                    b,
                    fieldInfo.clientNameComparison && clientNameComparison,
                    fieldInfo.timeStampComparison && timeStampComparison,
                );
            case "boolean":
                return this.compareBool(
                    aValue,
                    bValue,
                    dir,
                    a,
                    b,
                    fieldInfo.clientNameComparison && clientNameComparison,
                    fieldInfo.timeStampComparison && timeStampComparison,
                );
            case "timestamp":
                return this.compareTimestamp(
                    aValue,
                    bValue,
                    dir,
                    a,
                    b,
                    {
                        removeTime: fieldInfo.removeTime,
                    },
                    fieldInfo.clientNameComparison && clientNameComparison,
                    fieldInfo.timeStampComparison && timeStampComparison,
                );
            default:
                return 0;
        }
    }

    private sortChange(sort: SortDescriptor[], initialLoad = false) {
        this.sort.set(sort);
        if (!initialLoad) this.saveSortToLocalStorage();
        this.publishGridData();
    }

    public onSortChange(sort: SortDescriptor[]): void {
        this.sortChange(sort);
    }

    private updateAllViewColumnData() {
        const cardIds = this.data().map((card) => card.CardId);
        this.columnDataLayoutService.columnLoadedSubject.next({
            columnId: "all_view",
            cards: cardIds,
            fromCache: true,
        });
    }

    private compareFallBack(a: AllView, b: AllView, clientNameComparison = true, timeStampComparison = true): number {
        let res = 0;
        if (clientNameComparison) return this.sortField(a, b, "ClientName", "asc", false, true);
        if (timeStampComparison) return this.sortField(a, b, "TimeStamp", "desc", false, false);
        return res;
    }

    private compareBool(
        aValue: any,
        bValue: any,
        dir: "asc" | "desc",
        a: AllView,
        b: AllView,
        clientNameComparison = true,
        timeStampComparison = true,
    ): number {
        let res = (aValue ? 1 : 0) - (bValue ? 1 : 0);
        if (res !== 0) return dir === "desc" ? -res : res;

        return this.compareFallBack(a, b, clientNameComparison, timeStampComparison);
    }

    private compareString(
        aValue: any,
        bValue: any,
        dir: "asc" | "desc",
        a: AllView,
        b: AllView,
        clientNameComparison = true,
        timeStampComparison = true,
    ): number {
        let res;
        if (!aValue && bValue) return dir === "asc" ? -1 : 1;
        if (aValue && !bValue) return dir === "asc" ? 1 : -1;
        if (!aValue && !bValue) res = 0;
        else res = compareString(String(aValue), String(bValue));

        if (res !== 0) return dir === "desc" ? -res : res;

        return this.compareFallBack(a, b, clientNameComparison, timeStampComparison);
    }

    private compareNumber(
        aValue: any,
        bValue: any,
        dir: "asc" | "desc",
        a: AllView,
        b: AllView,
        clientNameComparison = true,
        timeStampComparison = true,
    ): number {
        const aAsNumber = Number(aValue);
        const bAsNumber = Number(bValue);
        if (!Number.isNaN(aAsNumber) && Number.isNaN(bAsNumber)) return dir === "desc" ? 1 : -1;
        else if (Number.isNaN(aAsNumber) && !Number.isNaN(bAsNumber)) return dir === "desc" ? -1 : 1;
        else if (!Number.isNaN(aAsNumber) && !Number.isNaN(bAsNumber)) {
            const res = aAsNumber - bAsNumber;
            if (res !== 0) return dir === "desc" ? -res : res;

            return this.compareFallBack(a, b, clientNameComparison, timeStampComparison);
        } else return this.compareString(aValue, bValue, dir, a, b, clientNameComparison, timeStampComparison);
    }

    private compareTimestamp(
        aValue: any,
        bValue: any,
        dir: "asc" | "desc",
        a: AllView,
        b: AllView,
        options: CompareTimestampOptions = {},
        clientNameComparison = true,
        timeStampComparison = true,
    ): number {
        let aAsNumber = Number(aValue);
        let bAsNumber = Number(bValue);
        if ("removeTime" in options && options.removeTime) {
            aAsNumber = this.getDateOnly(aAsNumber);
            bAsNumber = this.getDateOnly(bAsNumber);
        }

        return this.compareNumber(aAsNumber, bAsNumber, dir, a, b, clientNameComparison, timeStampComparison);
    }

    /**
     * To return the date only for a specific timestamp
     * @param timestamp Pass the timestamp number to return the number for date only
     * @returns return null or the timestamp with date only
     */
    private getDateOnly(timestamp: number): number {
        if (Number.isNaN(timestamp)) return timestamp;
        const date = new Date(timestamp * 1000); // Convertir en millisecondes
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth(); // 0-indexed
        const day = date.getUTCDate();

        // Créer une nouvelle date à minuit UTC
        const midnightUTC = new Date(Date.UTC(year, month, day, 0, 0, 0));

        // Retourner le timestamp Unix (en secondes)
        return Math.floor(this.helpersService.convertMillisecondsToSeconds(midnightUTC.getTime()));
    }

    protected onSelectedKeysChange(): void {
        this.mySelection = this.mySelection.filter((id) => this.cardData.has(id));
        const len = this.mySelection.length;
        this.helpersService.selectedCardsIds = this.mySelection;

        if (len === 0) {
            this.selectAllState = "unchecked";
        } else if (len > 0 && len < this.data().length) {
            this.selectAllState = "indeterminate";
        } else {
            this.selectAllState = "checked";
        }
    }

    protected onSelectAllChange(state: SelectAllCheckboxState): void {
        if (state === "checked") {
            this.mySelection = this.data().map((card) => card.CardId);
        } else {
            this.mySelection = [];
        }

        this.onSelectedKeysChange();
    }

    protected clearSelection() {
        this.mySelection = [];
        this.onSelectedKeysChange();
    }

    clearGridData() {
        this.viewGeneration++;
        this.refreshGeneration++;
        this.refreshIntent = "background";
        this.cancelDelayedCardRefresh();
        this.cancelDelayedGridPublication();
        this.resetViewportTracking();
        this.cardData.clear();
        this.cardRevisions.clear();
        this.AllCardView.set(new Map<string, AllView>());
        this.modelRevision = this.publishedRevision = 0;
        this.data.set([]);
        this.columns = [];
        this.skip.set(0);
        this.isGridDataAvailable.set(false);
        this.Group1 = "";
        this.Group2 = "";
    }

    private updateAllCardView(update: (cards: Map<string, AllView>) => void): void {
        this.AllCardView.update((currentCards) => {
            const nextCards = new Map(currentCards);
            update(nextCards);
            return nextCards;
        });
        this.modelRevision++;
    }

    private sortCards(): AllView[] {
        const cards = Array.from(this.AllCardView().values());
        const primarySort = this.sort()[0];
        if (primarySort?.field && primarySort.dir) {
            cards.sort((a, b) => this.sortField(a, b, primarySort.field, primarySort.dir));
        }
        return cards;
    }

    private publishGridData(data?: AllView[], skip = this.skip()): void {
        if (this.destroyed) return;

        this.cancelDelayedGridPublication();
        this.resetViewportTracking();
        const generation = this.viewGeneration;
        const revision = this.modelRevision;
        const snapshot = data ?? this.sortCards();
        this.AllCardView.set(new Map(snapshot.map((card) => [card.CardId, card])));
        this.data.set(snapshot);
        this.skip.set(Math.max(0, Math.min(skip, Math.max(0, snapshot.length - this.pageSize()))));
        this.publishedRevision = revision;
        this.onSelectedKeysChange();
        this.updateAllViewColumnData();
        if (this.destroyed || generation !== this.viewGeneration) return;
        this.isGridDataAvailable.set(true);
        this.loading.set(false);
    }

    private scheduleGridPublication(): void {
        if (this.destroyed) return;
        if (this.delayedGridPublicationTimer) clearTimeout(this.delayedGridPublicationTimer);
        this.delayedGridPublicationTimer = setTimeout(() => {
            this.delayedGridPublicationTimer = undefined;
            if (this.destroyed || this.modelRevision === this.publishedRevision) return;
            if (this.scrollEndSubscription || this.pendingViewportRead) return;

            const content = this.getGridContent();
            const anchor = this.isGridDataAvailable() && content ? this.getViewportAnchor(content) : undefined;
            if (anchor) this.publishAnchoredData(anchor);
            else this.publishGridData();
        }, 1000);
    }

    private cancelDelayedGridPublication(): void {
        if (this.delayedGridPublicationTimer) {
            clearTimeout(this.delayedGridPublicationTimer);
            this.delayedGridPublicationTimer = undefined;
        }
    }

    async loadCardData(card = null, deleted = false, refresh: "explicit" | "background" = "explicit") {
        if (this.destroyed) return;
        const generation = this.viewGeneration;

        if (card) {
            const revision = (this.cardRevisions.get(card.cardId) ?? 0) + 1;
            this.cardRevisions.set(card.cardId, revision);
            const isVisible = !deleted && this.filtersService.filterCards([card]).length > 0;
            if (isVisible) {
                this.cardData.set(card.cardId, card);
                const cardView = structuredClone(await this.createViewModel(card));
                if (
                    this.destroyed ||
                    generation !== this.viewGeneration ||
                    revision !== this.cardRevisions.get(card.cardId) ||
                    this.cardData.get(card.cardId) !== card
                ) {
                    return;
                }
                if (this.filtersService.filterCards([card]).length > 0) {
                    this.updateAllCardView((cards) => cards.set(card.cardId, cardView));
                } else {
                    this.cardData.delete(card.cardId);
                    this.updateAllCardView((cards) => cards.delete(card.cardId));
                }
            } else {
                this.cardData.delete(card.cardId);
                this.updateAllCardView((cards) => cards.delete(card.cardId));
            }

            this.onSelectedKeysChange();
            this.scheduleGridPublication();
            return;
        }

        if (refresh === "explicit") {
            this.resetViewportTracking();
            if (this.refreshIntent !== "rebuild") this.refreshIntent = "explicit";
        }
        const refreshGeneration = ++this.refreshGeneration;
        const revisions = new Map(this.cardRevisions);
        const cachedCards = await this.cardDisplayService.loadAllCards();
        if (this.destroyed || generation !== this.viewGeneration || refreshGeneration !== this.refreshGeneration) return;

        const cards: typeof this.cardData = new Map(this.filtersService.filterCards(cachedCards).map((card) => [card.cardId, card]));
        for (const [id, revision] of this.cardRevisions) {
            if (revision !== revisions.get(id)) {
                const current = this.cardData.get(id);
                if (current && this.filtersService.filterCards([current]).length > 0) cards.set(id, current);
                else cards.delete(id);
            }
        }
        this.cardData = cards;
        const prepared = await this.createAllViewModel(this.refreshIntent === "rebuild");
        if (!prepared || this.destroyed || generation !== this.viewGeneration || refreshGeneration !== this.refreshGeneration) return;
        const publish = this.refreshIntent !== "background" || !this.isGridDataAvailable();
        this.refreshIntent = "background";
        this.onSelectedKeysChange();
        if (!this.isGridDataAvailable()) {
            this.loadColumnName();
            this.skip.set(0);
        }
        if (publish) this.publishGridData();
        else this.scheduleGridPublication();
    }

    adjustLastColumn(fromResize = false) {
        if (this.viewAllGrid()?.columnList) {
            let width = 0;
            let firstSticky = true;
            this.viewAllGrid()?.columnList?.forEach((column) => {
                if ((!column.sticky || !firstSticky) && !column.hidden) {
                    width += column.width;
                }
                if (column.sticky) {
                    firstSticky = false;
                }
            });

            const grid = document.getElementsByTagName("kendo-grid");
            const clientWidth = grid.length > 0 ? grid[0].clientWidth - (fromResize ? 18 : 0) : 0;
            const colWidth = width > clientWidth ? 0 : clientWidth - width;
            firstSticky = true;
            this.viewAllGrid()?.columnList?.forEach((column) => {
                if (column.sticky && firstSticky) {
                    column.width = colWidth;
                    firstSticky = false;
                }
            });
        }
    }

    private checkAndUpdateSort(columnTitle) {
        const storageKey = this.getLocalStorageKey();
        const sortValue = localStorage.getItem(storageKey + "AllViewSort");
        if (!sortValue) return;

        const sortValueArray = JSON.parse(sortValue);
        if (sortValueArray.some((x) => x.field == columnTitle)) {
            this.sort.set([
                {
                    field: "TimeStamp",
                    dir: "desc",
                },
            ]);

            this.onSortChange(this.sort());
        }
    }
    onColumnVisibilityChange(event: any): void {
        const columnTitle = event.target.parentElement.innerText.replace(" ", "");
        this.viewAllGrid()?.columnList?.forEach((column) => {
            if (column.title == columnTitle) {
                const isChecked = event.target.checked;
                setTimeout(() => {
                    column.hidden = !isChecked;
                    if (isChecked) {
                        this.analyticsService.track("Add columns All View", { columnNames: column.title });
                    } else {
                        this.analyticsService.track("Delete columns All View", { columnNames: column.title });
                    }
                    this.setItemstoLocalStorage();
                    this.checkAndUpdateSort((column as ColumnComponent).field);
                    setTimeout(() => {
                        if (this.popupAnchor()?.nativeElement) this.container()?.show(this.popupAnchor()?.nativeElement);
                    });
                    this.adjustLastColumn();
                });
            }
        });
    }

    onSettingPageChange(e: any) {
        this.settingPageNumber = e;
        this.setSettingPagedItems();
    }

    public onShow(anchor: Element): void {
        this.searchText = "";
        this.setSettingPagedItems();
        this.container()?.toggle(anchor);
    }

    @ViewChild("searchInput") inputSearchRef!: ElementRef<HTMLInputElement> | undefined;

    onSearchClearInput = (): void => {
        if (this.inputSearchRef?.nativeElement) {
            (this.inputSearchRef?.nativeElement).value = "";
            (this.inputSearchRef?.nativeElement).focus();
        }
        this.searchText = "";
        this.settingPageNumber = 1;
        this.setSettingPagedItems();
    };

    onSearchInput = (event: Event): void => {
        const { value } = event.target as HTMLInputElement;
        this.searchText = value;
        this.settingPageNumber = 1;
        this.setSettingPagedItems();
    };

    onSearchButtonClick(event: Event): void {
        if (this.inputSearchRef?.nativeElement) {
            (this.inputSearchRef?.nativeElement).focus();
        }
    }

    public onShown(ev: PopoverShownEvent): void {
        ev?.popover?.popupElement?.classList.add("all-view-column-popup");

        requestAnimationFrame(() => {
            const triangleEl = ev?.popover?.popupElement?.querySelector(".k-popover-callout") as HTMLElement;
            if (triangleEl) {
                const triangleRect = triangleEl.getBoundingClientRect();
                const triangleWidth = triangleRect.width;

                const anchor = ev?.anchor as HTMLElement;
                let translateXForTriangle = 0;
                if (anchor) {
                    const button = anchor.closest("button");
                    if (button) {
                        const rect = button.getBoundingClientRect();
                        translateXForTriangle = rect.width / 2;
                        const viewportWidth = window.innerWidth;
                        translateXForTriangle += viewportWidth - rect.right - triangleWidth / 2;
                        ev?.popover?.popupElement?.style.setProperty("--translateXForTriangle", `${translateXForTriangle}px`);
                    }
                }
            }
        });
    }

    loadColumnName() {
        if (this.viewAllGrid()?.columnList) {
            setTimeout(() => {
                this.settingItems = [];
                this.viewAllGrid()?.columnList?.forEach((column) => {
                    if (!column.locked && !column.sticky) {
                        this.settingItems.push({
                            field: (column as ColumnComponent).field,
                            label: column.title,
                            selected: !column.hidden,
                            order: column.orderIndex,
                        });
                    }
                });

                this.settingItems.sort((a, b) => a.order - b.order);
                this.totalItemsCount = this.settingItems.length;
                this.setSettingPagedItems();
            });
        }
    }

    getLocalStorageKey() {
        let key = this.helpersService.getCurrentUser().sub;
        key = key + this.currentDashboard.Id;
        key = key + this.currentDashboard.TaxYear;
        key = key + "SummaryColumns";
        return key;
    }

    loadItemsFromLocalStorage() {
        const storageKey = this.getLocalStorageKey();
        const value = localStorage.getItem(storageKey);
        if (value) {
            this.settingItems = JSON.parse(value);
        }
    }

    setItemstoLocalStorage() {
        const storageKey = this.getLocalStorageKey();
        LocalStorageService.setItem(storageKey, JSON.stringify(this.settingItems));
    }

    public columnReorderHandler(args: ColumnReorderEvent): void {
        if (args.newIndex <= 3 || args.newIndex >= (this.viewAllGrid()?.columnList.toArray() ?? []).length - 1) {
            args.preventDefault();
        } else {
            this.loadColumnName();
            setTimeout(() => {
                this.setItemstoLocalStorage();
            });
            this.adjustLastColumn();
            this.analyticsService.track("Change order of a column in Summary view");
        }
    }

    private getClientName(card) {
        let clientName = card.contactInformation.lastName ?? "";
        if (clientName.length > 0) {
            clientName += ", ";
        }
        clientName += card.contactInformation.firstName;
        return clientName;
    }

    private async createTaxViewModel(card: any): Promise<AllView> {
        const category = this.getCategory(card);
        return {
            CardId: card.cardId,
            ClientCode: card.clientCodes,
            ClientName: this.getClientName(card),
            ReturnStatus: this.getStatusById(card.return.statusId),
            ReturnStatusOrder: this.returnStatuses.indexOf(card.return.statusId),
            Step: this.getStepNameFromStatusId(card.return.statusId),
            StepOrder: this.getStepOrderFromStatusId(card.return.statusId),
            StepStatusOrder: this.getStepStatusOrderFromStatusId(card.return.statusId),
            VIP: card.isVIP,
            Category: card.categoryId === null ? "Unassigned" : category?.Name,
            CategoryOrder: category ? category.Order : -1,
            ContactGroup1Caption: this.Group1,
            ContactGroup2Caption: this.Group2,
            ContactGroup1Id: card.group1Id,
            ContactGroup2Id: card.group2Id,
            ContactGroup1Option: this.getGroup1ById(card.group1Id),
            ContactGroup2Option: this.getGroup2ById(card.group2Id),
            DateReceived: this.handleDateFormat(card.documentsReceivedDate) ?? "",
            DateReceivedRaw: card.documentsReceivedDate,
            DateNeeded: card.dateNeeded
                ? this.helpersService.convertMillisecondsToSeconds(this.helpersService.getAdjustedDateFromUTC(card.dateNeeded).getTime())
                : card.dateNeeded,
            Deadline: card.deadLine
                ? this.helpersService.convertMillisecondsToSeconds(this.helpersService.getAdjustedDateFromUTC(card.deadLine).getTime())
                : card.deadLine,
            RoleUserMapping: card.roleUserMapping,
            TimeStamp: this.handleDateTimeFormat(card._ts),
            TimeStampRaw: card._ts,
            LastYearDocumentsReceivedDate: card.lastYearDocumentsReceivedDate,
            LastYearCompletedDate: card.lastYearCompletedDate,
            DeliveryMethod: this.readDeliveryMethods(card.deliveryMethod),
            CalcFields: this.cardDisplayService.calcCardFieldLabels(card, this.generateStatusIds(card)),
            return: card.return,
            t1: this.helpersService.eFileStatuses[card.efileStatuses.fedEfileStatus],
            tp1: this.helpersService.eFileStatuses[card.efileStatuses.provEfileStatus],
        };
    }

    private async createJobsBillinViewModel(card: any): Promise<AllView> {
        let priority = card.job.priority ?? null;
        if (priority in this.jobsAndBillingPriorityService.jobPriorityI18nKeys) {
            priority =
                this.jobsAndBillingPriorityService.jobPriorityI18nKeys[
                    priority as keyof typeof this.jobsAndBillingPriorityService.jobPriorityI18nKeys
                ];
        }

        return {
            CardId: card.cardId,
            ClientCode: {
                clientCodeiFirm: card.contactInformation.clientCode,
            },
            ClientName: this.getClientName(card),
            ReturnStatus: this.getJobStatusById(card.job.statusId, card.job.typeId),
            ReturnStatusOrder: this.returnStatuses.indexOf(card.job.statusId as never),
            Step: this.getStepNameFromStatusId(card.job.statusId),
            StepOrder: this.getStepOrderFromStatusId(card.job.statusId),
            StepStatusOrder: this.getStepStatusOrderFromStatusId(card.job.statusId),
            // Contact Information
            ContactGroup1Caption: this.translateService.instant("JobsAndBilling.Contact.Title") + " " + this.Group1,
            ContactGroup2Caption: this.translateService.instant("JobsAndBilling.Contact.Title") + " " + this.Group2,
            ContactGroup1Id: card.contactInformation.group1,
            ContactGroup2Id: card.contactInformation.group2,
            ContactGroup1Option: this.getGroup1ById(card.contactInformation.group1),
            ContactGroup2Option: this.getGroup2ById(card.contactInformation.group2),
            ContactManagerId: String(card.contactInformation.manager),
            ContactManager: this.iFirmRolesService.getUserNameById("1", card.contactInformation.manager),
            ContactPartnerId: String(card.contactInformation.partner),
            ContactPartner: this.iFirmRolesService.getUserNameById("2", card.contactInformation.partner),
            // Job
            JobID: card.job.id,
            JobType: this.wkTranslatePipe.transform(this.jobsAndBillingJobTypesService.jobTypes().get(card.job.typeId)?.name ?? "") ?? "",
            JobGroup1Caption: this.translateService.instant("JobsAndBilling.Job") + " " + this.Group1,
            JobGroup2Caption: this.translateService.instant("JobsAndBilling.Job") + " " + this.Group2,
            JobGroup1Id: card.job.group1,
            JobGroup2Id: card.job.group2,
            JobGroup1Option: this.getGroup1ById(card.job.group1),
            JobGroup2Option: this.getGroup2ById(card.job.group2),
            PriorityRaw: card.job.priority ?? null,
            Priority: priority ? this.translateService.instant(priority) : "",
            CurrentlyResponsibleId: String(card.job.currentlyResponsible),
            CurrentlyResponsible: this.usersService.getUserNameFromId()(card.job.currentlyResponsible ?? ""),
            JobManagerId: String(card.job.manager),
            JobManager: this.iFirmRolesService.getUserNameById("1", card.job.manager),
            JobPartnerId: String(card.job.partner),
            JobPartner: this.iFirmRolesService.getUserNameById("2", card.job.partner),
            JobOwnerId: String(card.job.owner),
            JobOwner: this.usersService.getUserNameFromId()(card.job.owner ?? ""),
            TargetStartDate: this.handleDateFormat(card.job.targetStartDate) ?? "",
            TargetEndDate: this.handleDateFormat(card.job.targetEndDate) ?? "",
            PeriodEndDate: this.handleDateFormat(card.job.periodEndDate) ?? "",
            TargetStartDateRaw: String(card.job.targetStartDate ?? ""),
            TargetEndDateRaw: String(card.job.targetEndDate ?? ""),
            PeriodEndDateRaw: String(card.job.periodEndDate ?? ""),

            TimeStamp: this.handleDateTimeFormat(card._ts),
            TimeStampRaw: card._ts,
            DateReceived: this.handleDateFormat(card.documentsReceivedDate) ?? "",
            DateReceivedRaw: card.documentsReceivedDate,
            CalcFields: this.cardDisplayService.calcCardFieldLabels(card, this.generateStatusIds(card)),
        };
    }

    private async createViewModel(card): Promise<AllView> {
        if (isJobsBilling(this.currentDashboard?.ApplicationType)) {
            return await this.createJobsBillinViewModel(card);
        } else {
            return await this.createTaxViewModel(card);
        }
    }

    public async createAllViewModel(force = true): Promise<boolean> {
        const generation = this.viewGeneration;
        const refreshGeneration = this.refreshGeneration;
        const cards = new Map(this.cardData);
        const revisions = new Map(this.cardRevisions);
        const updated = new Map<string, AllView>();
        for (const [id, card] of cards) {
            if (force || !this.AllCardView().has(id) || card._ts !== this.AllCardView().get(id)?.TimeStampRaw) {
                updated.set(id, structuredClone(await this.createViewModel(card)));
                if (this.destroyed || generation !== this.viewGeneration || refreshGeneration !== this.refreshGeneration) return false;
            }
        }

        for (const id of updated.keys()) {
            if (this.cardData.get(id) !== cards.get(id) || this.cardRevisions.get(id) !== revisions.get(id)) updated.delete(id);
        }
        const deletedIds = Array.from(this.AllCardView().keys()).filter((id) => !this.cardData.has(id));
        if (updated.size > 0 || deletedIds.length > 0) {
            for (const id of updated.keys()) this.cardRevisions.set(id, (this.cardRevisions.get(id) ?? 0) + 1);
            this.updateAllCardView((models) => {
                for (const id of deletedIds) models.delete(id);
                for (const [id, model] of updated) models.set(id, model);
            });
        }
        return true;
    }

    public async refreshAllViewModel(): Promise<void> {
        const generation = this.viewGeneration;
        const refreshGeneration = ++this.refreshGeneration;
        this.refreshIntent = "rebuild";
        this.resetViewportTracking();
        this.columns = [];
        this.Group1 = "";
        this.Group2 = "";
        this.setGroups();
        this.setRoles();
        const prepared = await this.createAllViewModel();
        if (!prepared || this.destroyed || generation !== this.viewGeneration || refreshGeneration !== this.refreshGeneration) return;
        this.refreshIntent = "background";
        this.publishGridData();
    }

    readDeliveryMethods(id) {
        if (id && this.deliveryMethods) {
            if (this.deliveryMethods[id]) {
                return this.deliveryMethods[id][this.helpersService.getLang()];
            }
        } else {
            return null;
        }
    }

    handleDateFormat(ts) {
        if (ts) {
            return this.datePipe.transform(new Date(ts * 1000), this.dateFormat, "", this.currentLanguage);
        } else {
            return null;
        }
    }

    handleDateTimeFormat(ts) {
        if (ts) {
            return this.datePipe.transform(new Date(ts * 1000), this.dateTimeFormat, "", this.currentLanguage);
        } else {
            return null;
        }
    }

    generateStatusIds(cardData) {
        const statusData = this.getCompleteStatusDataForCard(cardData);
        const statusIds = [];
        if (statusData) {
            statusData.Statuses.forEach((status) => {
                if (status) {
                    statusIds.push(status.TaxReturnStatusId);
                }
            });
        }

        return statusIds;
    }

    getCompleteStatusDataForCard(cardData) {
        const statusId = this.dashboardsService.isJobsBilling() ? cardData.job.statusId : cardData.return.statusId;
        const step = this.steps.get(statusId);
        return step;
    }

    setRoles() {
        const Roles = this.iFirmRolesService.iFirmRoles;
        Roles.forEach((role) => {
            this.columns.push({
                RoleId: role.Id,
                RoleName: role.Description[this.helpersService.getLang()],
            });
        });
        setTimeout(() => {
            this.loadItemsFromLocalStorage();
            this.setColumnOrder(this.settingItems);
        });
    }

    setGroups() {
        this.Group1 = this.iFirmGroupsService.getGroup1Caption(this.helpersService.getLang());
        this.Group2 = this.iFirmGroupsService.getGroup2Caption(this.helpersService.getLang());
        if (this.isiFirmDashboard || this.isJobsBillingDashboard) {
            this.isGroup1Enabled = this.iFirmGroupsService.getGroup1Enabled() && this.iFirmGroupsService.getGroup1ContactEnabled();
            this.isGroup2Enabled = this.iFirmGroupsService.getGroup2Enabled() && this.iFirmGroupsService.getGroup2ContactEnabled();
        } else {
            this.isGroup1Enabled = this.iFirmGroupsService.getGroup1Enabled();
            this.isGroup2Enabled = this.iFirmGroupsService.getGroup2Enabled();
        }
        this.isJobsGroup1Enabled = this.iFirmGroupsService.getGroup1Enabled() && this.iFirmGroupsService.getGroup1JobsEnabled();
        this.isJobsGroup2Enabled = this.iFirmGroupsService.getGroup2Enabled() && this.iFirmGroupsService.getGroup2JobsEnabled();
    }

    getCategory(card) {
        // null means the user explicitly selected "Unassigned" - do not calculate from tags.
        // (Matches the categoryId semantics used across the card modal and card-display service.)
        if (card.categoryId === null) {
            return null;
        }
        let category = this.categories[card.categoryId];
        const categoryIsValid = category && card.categoryId !== "Unassigned";
        if (!categoryIsValid) {
            const assignCategoryNoTags =
                this.currentDashboard.TaxYear < 2025 ? this.currentDashboard.AssignCategoryNoTags : this.currentDashboard.AssignCategoryId;
            category = this.categoriesService.calculateCategoryWithCategories(this.categoriesFromService, assignCategoryNoTags, card.tags);
        }
        return category;
    }

    getRoleData(data, roleId) {
        if (!data || !Array.isArray(data)) return;
        for (const singleData of data) {
            if (singleData.roleId == roleId) {
                return isUnassigned(singleData.userId) ? "" : this.iFirmRolesService.getUserNameById(roleId, singleData.userId);
            }
        }
    }

    getUserData(roleUserMapping, roleId: string): boolean {
        if (!roleUserMapping) return false;
        for (const roleUserEntry of roleUserMapping) {
            if (roleUserEntry.roleId == roleId) {
                return this.getUserActiveByRole(roleUserEntry.userId, roleId);
            }
        }
        return false;
    }

    getUserActiveByRole(userId: string | number | undefined | null, roleId: string): boolean {
        if (userId == null || userId === "" || userId == UnassignedUserId) return true;
        const userIdStr = String(userId);
        const role = this.iFirmRolesService.rolesWithUsers().get(roleId);
        const canonicalUserId = this.usersService.getUserById(userIdStr)?.Id ?? userIdStr;
        return role?.Users.get(canonicalUserId)?.Active ?? false;
    }

    getGroup1ById(id: number | string | undefined): string {
        if (typeof id === "string") id = Number(id);
        if (!id) id = -1;
        const groupObj = this.iFirmGroupsService.getGroup1Options();
        if (groupObj) {
            for (const group of groupObj) {
                if (group.Id == id) {
                    const lang = this.helpersService.getLang();
                    return lang in group ? (group as any)[lang] : "";
                }
            }
        }
        return this.translateService.instant("General.None");
    }

    getGroup1ActiveById(id: number | string | undefined): boolean {
        if (typeof id === "string") id = Number(id);
        if (!id) id = -1;
        const groupObj = this.iFirmGroupsService.getGroup1Options();
        if (groupObj) {
            for (const group of groupObj) {
                if (group.Id === id) {
                    return group.IsVisible;
                }
            }
        }
        return false;
    }

    getGroup2ActiveById(id: number | string | undefined): boolean {
        if (typeof id === "string") id = Number(id);
        if (!id) id = -1;
        const groupObj = this.iFirmGroupsService.getGroup2Options();
        if (groupObj) {
            for (const group of groupObj) {
                if (group.Id === id) {
                    return group.IsVisible;
                }
            }
        }
        return false;
    }

    getGroup2ById(id: number | string | undefined): string {
        if (typeof id === "string") id = Number(id);
        if (!id) id = -1;
        const groupObj = this.iFirmGroupsService.getGroup2Options();
        if (groupObj) {
            for (const group of groupObj) {
                if (group.Id === id) {
                    const lang = this.helpersService.getLang();
                    return lang in group ? (group as any)[lang] : ((group as any)["en-CA"] ?? "");
                }
            }
        }
        return this.translateService.instant("General.None");
    }

    getUserActiveById(userId) {
        const getUserFromId = this.usersService.getUserFromId();
        const user = getUserFromId(userId);
        if (user) {
            return user.Active;
        } else if (userId == UnassignedUserId) {
            return true;
        } else {
            return false;
        }
    }

    getStatusById(statusId: string): any {
        const taxReturn = this.taxReturnStatusId.get(statusId);
        if (taxReturn) {
            if (!taxReturn.Active || !this.statuses.get(statusId)) {
                return this.translateService.instant("General.Inactive") + taxReturn.Name[this.helpersService.getLang()];
            } else {
                return taxReturn.Name;
            }
        } else {
            return this.translateService.instant("General.Deleted");
        }
    }

    private getJobStatusById(jobStatusId: string, jobTypeId: number): string {
        const jobStatus = this.taxReturnStatusId.get(jobStatusId);
        if (!jobStatus) return "";

        let jobStatusTitle = this.wkTranslatePipe.transform(jobStatus.Name);
        if (!jobStatusTitle) return "";

        const associationsByJobType = this.jobsAndBillingJobTypesService.roleStatusAssociationByJobType();
        if (!this.taxReturnStatusId.size || !associationsByJobType.size) {
            return jobStatusTitle;
        }

        const statusIdsForJobType = associationsByJobType.get(jobTypeId) ?? new Set<string>();

        const activeMappedStatuses = Array.from(this.taxReturnStatusId.values()).filter((status) => this.filterStatuses(status, statusIdsForJobType));
        if (!activeMappedStatuses.some((status) => status.Id === jobStatusId)) {
            jobStatusTitle = this.translateService.instant("General.Inactive") + jobStatusTitle;
        }
        return jobStatusTitle;
    }

    private filterStatuses(status: ITaxReturnStatus, statusIdsAssociatedWithJobType: Set<string>): boolean {
        return !!status?.Id && status.Active && this.isStatusMapped(status.Id, statusIdsAssociatedWithJobType);
    }

    private isStatusMapped(statusId: string, statusIdsAssociatedWithJobType: Set<string>): boolean {
        return !!this.mappingOfStatusesService.getStepFromStatusId(statusId) && statusIdsAssociatedWithJobType.has(statusId);
    }

    getStepNameFromStatusId(statusId: string): any {
        const step = this.steps.get(statusId);
        if (step) {
            return step.Name;
        }
    }

    getStepOrderFromStatusId(statusId: string): any {
        const step = this.steps.get(statusId);
        if (step) {
            if (step.State === "notStarted") {
                return -(11000 - step.Order);
            } else if (step.State === "completed") {
                return step.Order + 10000;
            } else {
                return step.Order;
            }
        }
    }

    getStepStatusOrderFromStatusId(statusId: string): any {
        const step = this.steps.get(statusId);
        if (step) {
            return step.Statuses.findIndex((status) => status.TaxReturnStatusId === statusId);
        }
    }

    gridUserSelectionChange(gridUser, selection) {
        if (selection.selectedRows.length > 0) {
            const selectedData = selection.selectedRows[0].dataItem;
            this.router.navigate(["/" + this.router.url, selectedData.CardId]);
        }
    }

    public showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if ((element.nodeName === "TD" || element.className === "k-column-title") && element.offsetWidth < element.scrollWidth) {
            this.tooltipDir()?.show(element);
        } else if (
            element.nodeName === "SPAN" &&
            element.parentElement?.nodeName === "TD" &&
            element.parentElement?.offsetWidth < element.parentElement?.scrollWidth
        ) {
            this.tooltipDir()?.show(element.parentElement);
        } else {
            this.tooltipDir()?.hide();
        }
    }

    public showSettingPopoverTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if (element.nodeName === "LABEL" && element.offsetWidth < element.scrollWidth) {
            const tooltip = this.tooltipDir();
            if (tooltip) {
                tooltip.tooltipWidth = element.scrollWidth;
                tooltip.toggle(element);
            }
        } else {
            this.tooltipDir()?.hide();
        }
    }

    public cellClickHandler(args: CellClickEvent): void {
        if (args.columnIndex) {
            if (args.columnIndex > 0) {
                this.router.navigate(["/" + this.router.url, args.dataItem.CardId]);
                this.modalCoordinationService.closeAllModals();
            }
        }
    }

    public toggleActionsMenu(): void {
        this.isActionsMenuOpen.update((isOpen) => !isOpen);
    }

    public close_ExportPopup() {
        this.isExportPopupOpened = false;
    }

    public open_ExportPopup() {
        this.isExportPopupOpened = true;
    }

    getColumnToExportLabel(k: string): string | string[] {
        const columnToExport = this._fields().get(k);
        if (!columnToExport) return "";
        if (k === "RoleUserMapping") {
            return this.columns.map((x) => x.RoleName);
        } else if ("resource" in columnToExport && columnToExport.resource) {
            return this.translateService.instant("AllView." + (columnToExport.resource ? columnToExport.resource : k));
        }

        return columnToExport.label ? columnToExport.label : "";
    }

    getColumnsToExportMerged() {
        let columnsMerged: any[] = [];
        const orderAssigned = this.settingItems.find((it) => it.order !== 0);
        const ItemsOrder = this.settingItems;
        if (!orderAssigned) ItemsOrder.forEach((it) => (it.order = this.initialItemsOrder.find((it2) => it2.field === it.field)?.order ?? 0));

        Array.from(this._fields().keys()).forEach((k) => {
            const label = this.getColumnToExportLabel(k);
            const field = this._fields().get(k);
            if (!field || !field.isVisible) return;

            if (Array.isArray(label)) {
                columnsMerged = columnsMerged.concat(
                    label.map(
                        (l) => ({ key: k, label: l, setting: ItemsOrder.find((x) => x.label === l) }) as { key: string; label: string; setting: any },
                    ) as [],
                );
            } else {
                columnsMerged.push({ key: k, label, setting: ItemsOrder.find((x) => x.field === field.field) } as any);
            }
        });
        return columnsMerged;
    }

    exportView() {
        try {
            this.analyticsService.track("Exported All View");
            const separator = ",";
            let currentLang = this.helpersService.getLang();
            if (!currentLang) {
                currentLang = "en-CA";
            }
            const columnsMerged = this.getColumnsToExportMerged();
            const keys = Array.from(this._fields().keys());
            const columnsToExport = columnsMerged
                .filter((c) => (c.setting ? c.setting.selected : true))
                .sort((a, b) => {
                    const orderA = a.setting ? a.setting.order : keys.indexOf(a.key);
                    const orderB = b.setting ? b.setting.order : keys.indexOf(b.key);
                    return orderA - orderB;
                });
            const csvContent =
                "\uFEFF" +
                columnsToExport.map((k) => k.label).join(separator) +
                "\n" +
                this.data()
                    .map((row) => {
                        return columnsToExport
                            .map((c) => {
                                let k = c.key;
                                const columnToExport = this._fields().get(k);
                                if (!columnToExport) return "";

                                const cell = !("valueFunc" in columnToExport)
                                    ? (this.getValueForSort(row, columnToExport.valueField) ?? "")
                                    : (columnToExport.valueFunc?.(row) ?? "");
                                switch (columnToExport.type) {
                                    case "boolean":
                                        return cell ? this.translateService.instant("General.Yes") : this.translateService.instant("General.No");
                                    default:
                                        return cell && cell !== "undefined" ? String(cell).replace(",", "") : "";
                                }
                            })
                            .join(separator);
                    })
                    .join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const fileName =
                this.translateService.instant("Export.ApplicationName") +
                "-" +
                applicationStringFromApplicationType(this.currentDashboard?.ApplicationType).replaceAll(" ", "") +
                (!this.isJobsBillingDashboard ? "-" + this.currentDashboard?.TaxYear.toString() : "") +
                "-" +
                this.currentDashboard?.Title +
                "-" +
                this.translateService.instant("Export.CsvReportName") +
                "-" +
                this.datePipe.transform(Date.now(), "yyyyMMdd-HH-mm-ss") +
                ".csv";
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.responseErrorHandlerService.showPopup({
                typeLayout: "EXPORT-SUCCESS",
                messageCode: "csv",
            });
        } catch (er) {
            console.error("[WK] Error exporting CSV", er);
            this.responseErrorHandlerService.showPopup({
                typeLayout: "EXPORT-ERROR",
                messageCode: "csv",
            });
        } finally {
            this.close_ExportPopup();
        }
    }

    public close() {
        this.opened = false;
        this.CurrentDeletionCount = [];
    }

    public open() {
        this.CurrentDeletionCount = [];
        this.setSelectedDeletionCount();
        this.opened = true;
    }

    public async handleDelete() {
        this.analyticsService.track("Delete All View", { nbTrackingCards: this.mySelection.length });
        const promises = [];
        let deleted = [];
        this.opened = false;

        const uniqueSelection = this.mySelection.reduce((acc: string[], cardId: string) => {
            const card = this.cardDisplayService.getCardById(cardId);
            let alreadyIncluded = acc.includes(cardId);

            if (isTaxprepT1Card(card) && card?.members && card.members.length > 0) {
                card.members.forEach((member) => {
                    alreadyIncluded ||= acc.includes(member.cardId);
                });
            }
            if (!alreadyIncluded) {
                acc.push(cardId);
            }
            return acc;
        }, [] as string[]);

        uniqueSelection.forEach((cardId) => {
            promises.push(this.deleteCard(cardId));
        });

        await Promise.all(promises).then((results) => {
            results.forEach((result) => {
                deleted = deleted.concat(result.cardsDeleted);
            });
        });

        if (this.isiFirmDashboard && deleted.length > 0) {
            while (deleted.length > 0) {
                const chunk = deleted.splice(0, 100);
                await this.ifirmTaxCardsService.deleteCards(this.currentDashboard.Id, chunk);
            }
        } else if (this.isJobsBillingDashboard && deleted.length > 0) {
            for (const card of deleted) {
                await this.jobsAndBillingCommonService.deleteCard(card);
            }
        }

        this.mySelection = [];
        this.selectAllState = "unchecked";
        this.close();
    }

    setSelectedDeletionCount() {
        this.mySelection.forEach((cardId) => {
            if (!this.CurrentDeletionCount.includes(cardId)) {
                this.CurrentDeletionCount.push(cardId);
            }
            const card = this.cardDisplayService.getCardById(cardId);
            if (isTaxprepT1Card(card) && card?.members && card.members.length > 0) {
                card.members.forEach((member) => {
                    if (!this.CurrentDeletionCount.includes(member.cardId)) {
                        this.CurrentDeletionCount.push(member.cardId);
                    }
                });
            }
        });
    }

    async deleteCard(cardId: string) {
        const cardIds = [];
        const deletedIds = [];
        const card = this.cardDisplayService.getCardById(cardId);
        try {
            this.analyticsService.track("Deleted a card");
            cardIds.push(cardId);
            try {
                deletedIds.push(
                    await firstValueFrom(
                        this.cardService.deleteCard(cardId).pipe(
                            map(() => {
                                return cardId;
                            }),
                        ),
                    ),
                );
            } catch (err) {
                if (err.error && err.error.Message === "Too Many Requests") {
                    this.responseErrorHandlerService.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "429TooManyRequests",
                    });
                }
            }

            if (!this.isJobsBillingDashboard && isTaxprepT1Card(card)) {
                // Delete card members also
                for (const member of card.members) {
                    cardIds.push(member.cardId);
                    try {
                        await firstValueFrom(
                            this.cardService.deleteCard(member.cardId).pipe(
                                map(() => {
                                    return member.cardId;
                                }),
                            ),
                        );
                        deletedIds.push(member.cardId);
                    } catch (err) {
                        if (err.error && err.error.Message === "Too Many Requests") {
                            this.responseErrorHandlerService.showPopup({
                                typeLayout: "ERROR",
                                messageCode: "429TooManyRequests",
                            });
                        }
                    }
                }
            }
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "DELETE-ERROR",
                messageCode: "DELETE",
                additionInfo: this.translateService.instant("AllView.Events.ErrorMsg"),
            });
        }
        return { cardsProcessed: cardIds, cardsDeleted: deletedIds };
    }

    openBulkChange(): void {
        if (this.hasPendingFilterChanges) {
            this.bsModalRef = this.modalService.show(PendingChangesPopupComponent, {
                initialState: {
                    onDiscard: () => {
                        this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
                        this.helpersService.revertToFavoriteFilterEmitter.next();
                        this.modalCoordinationService.closeAllModals();
                        this.openBulkChangeModal();
                    },
                    onKeepEditing: () => {
                        // Just close modal
                    },
                },
                class: "custom-modal-rename-dashboard",
            });
            // Pass the modal ref to the component after it's created
            if (this.bsModalRef.content) {
                this.bsModalRef.content.bsModalRef = this.bsModalRef;
            }
        } else {
            this.modalCoordinationService.closeAllModals();
            this.openBulkChangeModal();
        }
    }

    private openBulkChangeModal(): void {
        this.isBulkChangeOpen.set(true);
    }

    public handleBulkChangeClose(): void {
        this.isBulkChangeOpen.set(false);
    }

    protected trackById(index: number, item: GridItem): string | number {
        return item.type === "data" ? (item.data as AllView).CardId : index;
    }
}
